import Chart from "react-apexcharts";

import Loader from "../../../../components/Loader";
import { useQuery } from "@tanstack/react-query";
import MetricsAPI from "@/api/Metrics/Metrics.js";
import styled, { useTheme } from "styled-components";
import getRandomColor from "@/utils/getRandomColor.js";
import CaseStatusApi from "@/api/case-status";
import { CHART_LEGEND_HEIGHT, denseChartHeight } from "./chartLayout";
import { useContainerWidth } from "@/hooks";

interface CaseLead {
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  user_id: number;
  // A user with no open cases yields `{ null: null }`: the metric LEFT JOINs open
  // cases onto all active users, so JSON_ARRAYAGG emits one null row for them.
  case_status_totals: Record<string, number> & { null?: null };
}

interface CaseStatus {
  case_status_id: number;
  status_name: string;
  total: number;
}

const CaseTypesPerUserWidget = styled(({ className }) => {
  const theme: any = useTheme();
  const [wrapRef, width] = useContainerWidth<HTMLDivElement>();
  const { data: caseLeadData } = useQuery({
    queryKey: ["caseTypesPerUser"],
    queryFn: () => MetricsAPI.getMetrics({ metric: "caseTypesPerUser" }),
    select: (data) => data?.data?.caseTypesPerUser || [],
  });

  const { data: caseStatuses } = useQuery({
    queryKey: ["caseStatuses"],
    queryFn: () => CaseStatusApi.getCaseStatuses(),
  });

  if (!(caseLeadData && caseStatuses)) return <Loader />;

  // Series and colors must come from the same filtered list. Building colors
  // from every status while the series skip "Closed" shifts the two arrays out of
  // alignment, so every series after "Closed" took the next status's color.
  const visibleStatuses = caseStatuses.filter(
    (status: CaseStatus) => status.status_name !== "Closed"
  );

  const statusNames = visibleStatuses.map(
    (status: CaseStatus) => status.status_name
  );

  // Users with no open cases come back as `{ null: null }` from the metric's LEFT
  // JOIN, so total them defensively and rank rather than keep `user_id` order.
  const userTotal = (d: CaseLead) =>
    statusNames.reduce(
      (sum: number, status: string) =>
        sum + (d.case_status_totals?.[status] || 0),
      0
    );

  const sortedUsers = [...caseLeadData].sort(
    (a: CaseLead, b: CaseLead) => userTotal(b) - userTotal(a)
  );

  const seriesData = statusNames.map((status: string) => ({
    name: status,
    data: sortedUsers.map((d: CaseLead) => d.case_status_totals?.[status] || 0),
  }));

  const colors = visibleStatuses.map((status: CaseStatus) =>
    getRandomColor(
      status.status_name,
      status.case_status_id,
      theme.name === "DARK" ? "dark" : "bright"
    )
  );

  const categories = sortedUsers.map((d: CaseLead) => d.full_name);

  return (
    <div className={className} ref={wrapRef}>
      {width > 0 && (
        <Chart
          options={{
            colors,
            chart: {
              animations: {
                enabled: false,
              },
              stacked: true,
              background: "unset",
              toolbar: {
                show: false,
              },
            },
            plotOptions: {
              bar: {
                borderRadius: 4,
                horizontal: true,
                dataLabels: {
                  position: "bottom", // top, center, bottom
                },
              },
            },
            theme: {
              mode: theme.name === "DARK" ? "dark" : "light",
              palette: "palette8",
            },
            fill: {
              colors,
              type: "solid",
              opacity: theme.name === "DARK" ? 0.33 : 1,
            },
            stroke: {
              colors: [theme.palette.primary.main + "90"],
              width: 1,
            },
            dataLabels: {
              enabled: true,
              textAnchor: "start",
              style: {
                fontSize: "12px",
                fontWeight: "bold",
                colors: ["#fff"],
              },
            },
            grid: {
              show: false,
              padding: { top: 0, right: 0, bottom: 0, left: 0 },
            },
            yaxis: {
              labels: {
                show: true,
                // Bound the name gutter so a long name cannot squeeze the bars out.
                maxWidth: 160,
              },
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
            },
            xaxis: {
              categories,
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              labels: {
                show: false,
              },
            },
            legend: {
              position: "top",
              horizontalAlign: "left",
              offsetX: 40,
              // Pin the legend so a wrapped one cannot eat the plot area and
              // collapse the row pitch. See CHART_LEGEND_HEIGHT.
              height: CHART_LEGEND_HEIGHT,
            },
          }}
          type="bar"
          width={width}
          height={denseChartHeight(categories.length) + CHART_LEGEND_HEIGHT}
          series={seriesData}
        />
      )}
    </div>
  );
})`
  flex: 0 0 auto;
  min-width: 0;

  /* Apex hard sets the legend wrap to CHART_LEGEND_HEIGHT but gives it no
     overflow of its own, so a wrapped legend would spill over the plot. */
  .apexcharts-legend {
    overflow-y: auto;
    overflow-x: hidden;
    align-content: flex-start;
  }
`;

export default CaseTypesPerUserWidget;
